import { Link } from "react-router-dom";
import { Section, SectionHeading, FadeIn } from "@/components/Section";
import { ArrowRight } from "lucide-react";

const VALUES = [
  {
    title: "Infrastructure, not intervention",
    desc: "We build the operating layer around clinics — we never step into clinical decision-making, prescribing, or care delivery.",
  },
  {
    title: "Curation as a moat",
    desc: "We ship what works. Our catalog is a disciplined shortlist, not an unfiltered marketplace.",
  },
  {
    title: "Clinic ownership",
    desc: "Clinics own patient relationships, clinical decisions, and pricing. We make their operations leverageable.",
  },
  {
    title: "Compliance viability",
    desc: "Every program is assessed against compliance viability before it makes it into the platform.",
  },
  {
    title: "One operating surface",
    desc: "Clinics shouldn't have to leave Nodera to run their day. Vendor ordering, workflow, invoicing, and oversight live in one place — by default.",
  },
];

const ONE_SURFACE_NOTES = [
  {
    title: "In-platform by default",
    desc: "Clinics deploy programs, dispatch vendor workflows, track fulfilment, and reconcile billing without ever leaving the portal. The fewer surfaces a clinic has to context-switch between, the more leverageable their operations become.",
  },
  {
    title: "External links are exceptions, not defaults",
    desc: "A small slice of services genuinely require a different ordering channel — compounded medications, for instance, often must route through a state-licensed 503A pharmacy portal. When a vendor has a legitimate reason, we surface a written justification next to the link so clinics know exactly why they're being routed off-platform.",
  },
  {
    title: "Every exception is reviewed",
    desc: "Vendors cannot publish an external order link without a written justification, and Nodera reviews each one before it becomes visible to clinics. Approving an exception is a deliberate decision — not a default.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Section className="pt-28 pb-10">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-4">About Nodera Health</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
            The infrastructure layer powering cash-pay clinic growth.
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Nodera Health is a software and operational enablement platform. We don't practice medicine. We build the operating layer that enables licensed clinics to deploy and scale high-margin cash-pay programs — through curated services, vendor coordination, workflow automation, and financial workflow enablement.
          </p>
        </div>
      </Section>

      <Section className="pt-0">
        <SectionHeading eyebrow="Principles" title="What we optimize for." />
        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {VALUES.map((v, i) => (
            <FadeIn key={i} delay={i * 0.06}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-6">
                <h3 className="font-display text-lg font-semibold text-slate-900">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="pt-0">
        <SectionHeading
          eyebrow="One operating surface"
          title="Why we keep clinics inside Nodera."
        />
        <div className="mt-6 max-w-3xl">
          <p className="text-base text-slate-600 leading-relaxed">
            Cash-pay clinic operations are already split across too many surfaces — scheduling
            tools, vendor portals, payments dashboards, intake forms, marketing systems. Nodera
            collapses the operational layer into a single platform so a clinic team can run a
            full cash-pay program without juggling five logins.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {ONE_SURFACE_NOTES.map((v, i) => (
            <FadeIn key={i} delay={i * 0.06}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-6">
                <h3 className="font-display text-lg font-semibold text-slate-900">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="pt-0">
        <SectionHeading eyebrow="Founder note" title="Why we started Nodera." />
        <FadeIn>
          <article
            className="mt-10 mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white p-8 sm:p-10"
            data-testid="about-founder-letter"
          >
            <div className="space-y-5 text-[15px] leading-relaxed text-slate-700 font-serif">
              <p className="font-display text-xl text-slate-900 not-italic">
                <span className="text-blue-600 mr-1 font-bold">"</span>
                Over 15 years partnering with independent and community medical clinics, I've seen the
                same pattern: dedicated teams delivering exceptional care while shouldering operational
                complexity that only grows heavier each year.
              </p>
              <p>
                Meanwhile, healthcare consumerism has changed the industry. Digital-first platforms
                and convenience-driven models now compete head-on for the patients traditional clinics
                have always served — and clinics need modern operational infrastructure to compete
                without giving up what makes them irreplaceable.
              </p>
              <p>
                That's why we built Nodera Health: to give independent clinics a turnkey operational
                layer for cash-pay programs, recurring engagement, and vendor coordination — so the
                personalized, relationship-driven care these clinics provide remains a meaningful
                option for the communities that depend on it.
                <span className="text-blue-600 ml-1 font-bold">"</span>
              </p>
            </div>
            <div className="mt-8 pt-6 border-t border-slate-100">
              <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">
                — Founder
              </div>
              <div className="font-display text-base font-semibold text-slate-900 mt-1">
                Nodera Health
              </div>
            </div>
          </article>
        </FadeIn>
      </Section>

      <Section className="bg-slate-900 text-white">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-300 mb-4">Positioning</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
              This is not a healthcare company. This is the infrastructure layer enabling clinics to deploy and scale cash-pay programs built on proven services, a stringently vetted vendor network, and competitive contracted rates negotiated across that network.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/demo"
              data-testid="about-cta-btn"
              className="inline-flex items-center gap-1.5 bg-white text-slate-900 rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-100 transition-colors"
            >
              Book a Demo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
